/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: useJackpotHoverPopup.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  15.05.23 10:48 am.
 * Last modified: 15.05.23 10:48 am.
 *
 * More information at: https://egt-digital.com/
 */

import React from 'react';
import { useEffect, useState } from 'react';
import HoverPopup from '../components/HoverPopup';
import { useSectionContainerRef } from '../contexts/SectionContainerRefContext';

interface IHoverHandlerComponentProps {
    level: number;
    style?: React.CSSProperties | undefined;
    arrowClass?: string;
    classes?: string[];
}

const useJackpotHoverPopup = ({
    jackpotName,
    denomination,
    currency,
}: {
    jackpotName: string;
    denomination: number;
    currency: string;
}): [
    (
        arg0: number,
    ) => {
        onMouseEnter?: any;
        onMouseLeave: any;
        onPointerUp: any;
    },
    React.FunctionComponent<IHoverHandlerComponentProps>,
] => {
    const [hoveredJackpot, setHoveredHackpot] = useState<number>(-1);

    const onHoverHandler = (level: number) => () => {
        setHoveredHackpot(level);
    };

    const offHoverHandler = (level: number) => () => {
        setHoveredHackpot(-1);
    };

    const handlers = (level: number) => {
        return {
            onMouseEnter: onHoverHandler(level),
            onPointerUp: onHoverHandler(level),
            onMouseLeave: offHoverHandler(level),
        };
    };

    const onScroll = (level: number) => {
        offHoverHandler(level)();
    };

    const Component = ({ level, style, arrowClass, classes }: IHoverHandlerComponentProps) => {
        const [sectionContainerRef] = useSectionContainerRef();

        useEffect(() => {
            const sectionContainer = sectionContainerRef?.current;
            if (sectionContainer && hoveredJackpot === level) {
                sectionContainer.addEventListener('scroll', () => onScroll(level));
            }
            return () => sectionContainer?.removeEventListener('scroll', () => onScroll(level));
        }, [level, sectionContainerRef]);

        return (
            <>
                {hoveredJackpot === level && (
                    <HoverPopup
                        jackpotName={jackpotName}
                        denomination={denomination}
                        currency={currency}
                        level={level}
                        style={style}
                        arrowClass={arrowClass}
                        classes={classes}
                    />
                )}
            </>
        );
    };

    return [handlers, Component];
};

export default useJackpotHoverPopup;
